import React, {FC, useState} from "react";
import moment from "moment";
import {Alert, Button, Input, Modal, Radio, Space, Spin, Tooltip, Typography, message} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {red} from "@ant-design/colors";
import {useGetActiveDunningQuery, useWithdrawDunningMutation} from "../../../../generated/graphql";
import i18n from "../../../../services/i18n";
import {resolveServerErrorMessage} from "../../../../helpers/resolveServerErrorMessage";
import {useHasRoleAtLeast} from "../../../../helpers/useIsStaffOnlyAccess";
import Roles from "../../../../../shared/models/roles";

// Getter statt Modul-Konstante (react-hot-loader, siehe BookingDunningsPanel)
const tr = () => i18n.containers.invoice.BookingDunnings;

type Props = {
    bookingId: string;
    scheduledPaymentId: string;
    /** Teilbezahlt oder erstattet: Löschen lehnt der Server ab, die Rücknahme lässt die Gebühr stehen */
    hasSettledMoney?: boolean;
    /** Bisheriges Löschen der Rate */
    deletePayment: () => Promise<void>;
    /** Nach der Rücknahme: Raten und Mahnungen neu laden */
    onWithdrawn: () => void;
    onClose: () => void;
};

/**
 * Löschen einer Mahngebühr (#1517): Gehört sie zur aktiven Stufe, fragt der Dialog, ob nur die Gebühr erlassen
 * (Mahnung bleibt) oder die Mahnung zurückgenommen werden soll (Rücknahme löscht die Gebühr mit). Sonst das
 * bisherige Löschen mit Hinweis.
 */
const DunningFeeDeleteModal: FC<Props> = ({bookingId, scheduledPaymentId, hasSettledMoney, deletePayment, onWithdrawn, onClose}) => {
    const {data, loading, error} = useGetActiveDunningQuery({variables: {bookingId}, fetchPolicy: "network-only"});
    // Raten der Buchung (Gebühr weg) und die Mahnkarte neu laden; nicht gemountete Queries ignoriert Apollo
    const [withdraw, {loading: withdrawing}] = useWithdrawDunningMutation({
        refetchQueries: ["GetUnpaidScheduledPayments", "GetPaidScheduledPayments", "GetBookingById", "GetBookingDunnings"],
        awaitRefetchQueries: true,
    });
    const isAdmin = useHasRoleAtLeast(Roles.Admin);
    const [choice, setChoice] = useState<"waive" | "withdraw">(hasSettledMoney ? "withdraw" : "waive");
    const [reason, setReason] = useState("");
    const [deleting, setDeleting] = useState(false);

    const dunning = data?.activeDunning;
    // Rücknahme nur für die Gebühr der aktiven Stufe und nur mit den Rechten der Mahnkarte
    const canWithdraw = isAdmin && Boolean(dunning) && dunning?.scheduledPaymentId === scheduledPaymentId;
    const level = dunning ? tr().levels[dunning.level]() : "";
    const date = dunning ? moment(dunning.createDate).format("DD.MM.YYYY") : "";

    const submit = async () => {
        if (canWithdraw && choice === "withdraw" && dunning) {
            if (!reason.trim()) {
                message.error(tr().withdraw.reasonRequired());

                return;
            }
            try {
                await withdraw({variables: {id: dunning.id, reason}});
                message.success(tr().withdraw.done());
                onClose();
                onWithdrawn();
            } catch (error) {
                message.error(resolveServerErrorMessage(error, tr().failed()));
            }

            return;
        }
        setDeleting(true);
        await deletePayment();
        setDeleting(false);
        onClose();
    };

    return (
        <Modal
            open
            title={tr().feeDelete.title()}
            okText={canWithdraw ? tr().feeDelete.ok() : i18n.containers.payments.OpenPaymentsList.deletePayment.delete()}
            // Ohne Mahnstand kein Löschen: sonst entfiele die Rückfrage (Codex #1596)
            okButtonProps={{danger: true, loading: withdrawing || deleting, disabled: loading || Boolean(error)}}
            cancelText={tr().feeDelete.cancel()}
            onOk={submit}
            onCancel={onClose}
        >
            {loading ? (
                <Spin />
            ) : error ? (
                <Alert type="error" showIcon message={resolveServerErrorMessage(error, tr().failed())} />
            ) : canWithdraw ? (
                <>
                    <Typography.Paragraph>{tr().feeDelete.text({level, date})}</Typography.Paragraph>
                    <Radio.Group value={choice} onChange={(e) => setChoice(e.target.value)}>
                        <Space direction="vertical">
                            <Radio value="waive" disabled={hasSettledMoney}>
                                {tr().feeDelete.waive()}
                                <Typography.Paragraph type="secondary" style={{marginBottom: 0}}>
                                    {tr().feeDelete.waiveHint()}
                                </Typography.Paragraph>
                            </Radio>
                            <Radio value="withdraw">
                                {tr().feeDelete.withdraw()}
                                <Typography.Paragraph type="secondary" style={{marginBottom: 0}}>
                                    {hasSettledMoney ? tr().feeDelete.withdrawHintPartiallyPaid() : tr().feeDelete.withdrawHint()}
                                </Typography.Paragraph>
                            </Radio>
                        </Space>
                    </Radio.Group>
                    {choice === "withdraw" && (
                        <Input.TextArea style={{marginTop: 12}} rows={3} maxLength={500} placeholder={tr().withdraw.reason()} value={reason} onChange={(e) => setReason(e.target.value)} />
                    )}
                </>
            ) : (
                <>
                    <Typography.Paragraph>{i18n.containers.payments.OpenPaymentsList.deletePayment.text()}</Typography.Paragraph>
                    {dunning && <Typography.Text type="warning">{tr().rate.deleteText({level, date})}</Typography.Text>}
                </>
            )}
        </Modal>
    );
};

/** Löschen-Knopf einer Mahngebühr in der Ratenliste; eigener State, damit RenderActions keine Hooks dazubekommt */
const DunningFeeDeleteButton: FC<Omit<Props, "onClose">> = (props) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <Tooltip title={i18n.containers.payments.OpenPaymentsList.deletePayment.title()} placement="left" mouseEnterDelay={0.75}>
                <Button size="small" type="link" style={{color: red[3]}} onClick={() => setOpen(true)}>
                    <DeleteOutlined />
                </Button>
            </Tooltip>
            {open && <DunningFeeDeleteModal {...props} onClose={() => setOpen(false)} />}
        </>
    );
};

export default DunningFeeDeleteButton;
